import { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import dayjs from "dayjs";
import { api, apiError } from "../../lib/api";
import { PageHeader, Card, Table, Td, Badge, Empty, Btn, Modal } from "../../components/UI";
import { ClinicalTabs, useClients, ClientSelect } from "./shared";
import { FLabel, FSelect } from "../engagement/FormControls";
import { Plus, Pencil, Trash2, Lock } from "lucide-react";
import { toast } from "sonner";
import { VersionAccordion } from "../../components/VersionAccordion";
import { ExportCsvBtn } from "../../components/ExportCsvBtn";

const ASSESS_COLS = 7;

export default function Assessments() {
  const navigate = useNavigate();
  const clients = useClients();
  const [templates, setTemplates] = useState([]);
  const [items, setItems] = useState([]);
  const [clientFilter, setClientFilter] = useState("");
  const [newOpen, setNewOpen] = useState(false);
  const [newForm, setNewForm] = useState({ client_id: "", template_id: "" });
  const [searchParams] = useSearchParams();
  useEffect(() => {
    const preselect = searchParams.get("client");
    if (preselect) {
      setClientFilter(preselect);
      setNewForm((f) => ({ ...f, client_id: preselect }));
      if (searchParams.get("new") === "1") setNewOpen(true);
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps
  const [confirmDelete, setConfirmDelete] = useState(null);
  const [busy, setBusy] = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => { api.get("/assessment-templates").then((r) => setTemplates(r.data)); }, []);
  const load = () => {
    const params = clientFilter ? { client_id: clientFilter } : {};
    api.get("/assessments", { params }).then((r) => { setItems(r.data); setLoading(false); });
  };
  useEffect(load, [clientFilter]); // eslint-disable-line react-hooks/exhaustive-deps

  const create = async () => {
    if (!newForm.client_id || !newForm.template_id) { toast.error("Select a client and an assessment"); return; }
    setBusy(true);
    try {
      const r = await api.post("/assessments", newForm);
      toast.success("Assessment started");
      navigate(`/clinical/assessments/${r.data.id}`);
    } catch (err) { toast.error(apiError(err)); }
    setBusy(false);
  };

  const doDelete = async () => {
    try {
      await api.delete(`/assessments/${confirmDelete.id}`);
      toast.success("Assessment deleted");
      setConfirmDelete(null); load();
    } catch (err) { toast.error(apiError(err)); }
  };

  return (
    <div>
      <PageHeader title="Assessments" subtitle="Structured clinical assessment instruments"
        action={<div className="flex items-center gap-2"><ExportCsvBtn resource="assessments" /><Btn data-testid="add-assessment-btn" onClick={() => setNewOpen(true)}><Plus size={15} /> New Assessment</Btn></div>} />
      <ClinicalTabs />
      <Card>
        <div className="flex flex-wrap gap-3 p-4">
          <FSelect data-testid="assess-client-filter" className="w-56" placeholder="All Clients" value={clientFilter} onChange={(e) => setClientFilter(e.target.value)}>
            {clients.map((c) => <option key={c.id} value={c.id}>{c.first_name} {c.last_name}</option>)}
          </FSelect>
        </div>
        <Table headers={["", "Assessment", "Client", "Status", "Started", "Last Updated", ""]}>
          {items.map((a) => (
            <VersionAccordion key={a.id} record={a} kind="assessment" colSpan={ASSESS_COLS}
              primaryTestId={`assess-row-${a.id}`} rowClassName="hover:bg-[#FDFCF8] transition-colors"
              renderPrimary={({ expandToggle, versionBadge }) => (
                <>
                  <Td className="w-8">{expandToggle}</Td>
                  <Td className="font-medium text-[#3E2723] cursor-pointer" onClick={() => navigate(`/clinical/assessments/${a.id}`)}>
                    <span className="flex items-center gap-1.5">{a.template_name}{versionBadge}</span>
                  </Td>
                  <Td className="cursor-pointer" onClick={() => navigate(`/clinical/assessments/${a.id}`)}>{a.client_name}</Td>
                  <Td><Badge value={a.status} /></Td>
                  <Td>{dayjs(a.created_at).format("MM/DD/YYYY")}</Td>
                  <Td>{dayjs(a.updated_at).format("MM/DD/YYYY")}</Td>
                  <Td>
                    <div className="flex gap-2 justify-end">
                      <button type="button" data-testid={`assess-open-${a.id}`} aria-label="Open" onClick={() => navigate(`/clinical/assessments/${a.id}`)}
                        className="h-8 w-8 rounded-full bg-[#F5F2EB] text-[#7B6157] flex items-center justify-center hover:bg-[#EDE7DC]">
                        {a.status === "signed" ? <Lock size={13} /> : <Pencil size={13} />}
                      </button>
                      {a.status !== "signed" && (
                        <button type="button" data-testid={`assess-delete-${a.id}`} aria-label="Delete" onClick={() => setConfirmDelete(a)}
                          className="h-8 w-8 rounded-full bg-[#F5F2EB] text-[#7B6157] flex items-center justify-center hover:bg-[#FFEBEE] hover:text-[#C62828]"><Trash2 size={13} /></button>
                      )}
                    </div>
                  </Td>
                </>
              )}
            />
          ))}
        </Table>
        {!loading && items.length === 0 && <Empty text="No assessments yet" />}
      </Card>

      <Modal open={newOpen} onClose={() => setNewOpen(false)} title="New Assessment">
        <div className="space-y-4">
          <div><FLabel>Client</FLabel><ClientSelect clients={clients} value={newForm.client_id} onChange={(v) => setNewForm((f) => ({ ...f, client_id: v }))} testId="assess-new-client" /></div>
          <div><FLabel>Assessment Instrument</FLabel>
            <FSelect data-testid="assess-new-template" value={newForm.template_id} onChange={(e) => setNewForm((f) => ({ ...f, template_id: e.target.value }))} placeholder="Select assessment">
              {templates.map((t) => <option key={t.id} value={t.id}>{t.name}</option>)}
            </FSelect>
          </div>
          {newForm.template_id && <p className="text-xs text-[#7B6157]">{templates.find((t) => t.id === newForm.template_id)?.description}</p>}
          <div className="flex justify-end gap-2">
            <Btn variant="ghost" onClick={() => setNewOpen(false)}>Cancel</Btn>
            <Btn data-testid="assess-create-btn" onClick={create} disabled={busy}>{busy ? "Starting…" : "Start Assessment"}</Btn>
          </div>
        </div>
      </Modal>

      <Modal open={!!confirmDelete} onClose={() => setConfirmDelete(null)} title="Delete Assessment">
        <p className="text-sm text-[#6D4C41] mb-5">Delete "<span className="font-semibold">{confirmDelete?.template_name}</span>" for {confirmDelete?.client_name}?</p>
        <div className="flex justify-end gap-2">
          <Btn variant="ghost" onClick={() => setConfirmDelete(null)}>Cancel</Btn>
          <Btn data-testid="assess-confirm-delete-btn" variant="danger" onClick={doDelete}>Delete</Btn>
        </div>
      </Modal>
    </div>
  );
}
